import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import { isBelowMarginLimit } from '../../utils/marginLimit';
import type {
  ProMargin,
  DriverPayLeg,
  LineItem,
} from '../../services/proDocuments.service';

/**
 * Per-Pro margin breakdown modal — the FastCMS-styled port of V1's showmargin
 * popup. Sections mirror V1 (main.pl:12846-13563): Customer Pay, Driver Pay,
 * Employee Driver Cost (when any payroll-hourly legs), Additional Costs (when
 * any carrier/accessorial costs), then the Profit-Loss + Margin summary.
 * Driven entirely by the {@link ProMargin} read-model that {@link useProMargin}
 * already fetches, so it needs no additional API call.
 *
 * Opened from the header "Margin" pill in ProDetailSlideOver (replacing the old
 * hover tooltip). Portals to <body> at z-[130] so it layers above the Pro
 * slide-over (z-[100]); an Escape handler runs in the capture phase and stops
 * propagation so the key closes this modal without also closing the drawer.
 */
interface Props {
  open: boolean;
  onClose: () => void;
  margin: ProMargin;
  /** True while a fresh margin pull is in flight (e.g. right after opening);
   *  shows a Loading… state instead of the (stale) breakdown. */
  loading?: boolean;
}

/** "$1,234.56" / "-$49.50" (sign outside the $, unlike toLocaleString's "$-"). */
function fmtUSD(n: number): string {
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Two-decimal percent for the profit/loss summary (matches V1's 48.67%). */
function fmtPct(n: number): string {
  return `${n.toFixed(2)}%`;
}

const TH =
  'text-left text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400';
const NUM = 'text-right tabular-nums';
const ROW_BORDER = 'border-t border-slate-100 dark:border-gray-700/60';

/** Section header bar (FastCMS take on V1's navy title strips). */
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-slate-100 dark:bg-gray-700/50 px-3 py-1.5 text-[0.75rem] font-bold uppercase tracking-wide text-slate-700 dark:text-gray-200">
    {children}
  </div>
);

/**
 * Rate (or the state, when not OK). V1 reserves red for MISSING RATE only —
 * DO NOT PAY is an intentional state and renders in the normal text color
 * (main.pl:13293-13304 / 13319-13330).
 */
function RateOrState({ rate, state }: { rate: number; state: LineItem['state'] }) {
  if (state === 'MISSING RATE') {
    return <span className="font-semibold text-red-600 dark:text-red-400">{state}</span>;
  }
  if (state === 'DO NOT PAY') {
    return <span>{state}</span>;
  }
  return <span>{fmtUSD(rate)}</span>;
}

/** Small "WE <date>" tag for a settled row — the pay-week ENDING date (actual
 * payment is ~12 days later). A leg's parts can settle on different weeks, so
 * this is rendered per-row (base leg and each paid line item). */
function WeTag({ isPaid, payrollDate }: { isPaid?: boolean; payrollDate?: string }) {
  if (!isPaid || !payrollDate) return null;
  return (
    <span className="ml-2 text-[0.625rem] font-normal text-slate-400 dark:text-gray-500 whitespace-nowrap">
      WE {payrollDate}
    </span>
  );
}

/** Item | Rate table with a bold Total row — shared by Customer Pay and
 * Additional Costs (identical V1 layout). */
const ItemRateSection: React.FC<{
  testid: string;
  title: string;
  items: LineItem[];
  total: number;
}> = ({ testid, title, items, total }) => (
  <section data-testid={testid} className="mb-4">
    <SectionTitle>{title}</SectionTitle>
    <table className="w-full">
      <thead>
        <tr>
          <th className={`${TH} py-1`}>Item</th>
          <th className={`${TH} ${NUM} py-1`}>Rate</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it, i) => (
          <tr key={`${testid}-${i}`} className={ROW_BORDER}>
            <td className="py-1">{it.item}</td>
            <td className={`${NUM} py-1`}>
              <RateOrState rate={it.rate} state={it.state} />
            </td>
          </tr>
        ))}
        <tr className={`${ROW_BORDER} font-bold`}>
          <td className="py-1">Total</td>
          <td className={`${NUM} py-1`}>{fmtUSD(total)}</td>
        </tr>
      </tbody>
    </table>
  </section>
);

const ProMarginModal: React.FC<Props> = ({ open, onClose, margin, loading = false }) => {
  // Capture-phase Escape: close THIS modal and stop the event before it reaches
  // the slide-over's own Escape handler (which would close the drawer).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Read-only margin breakdown: Alt+C closes, alongside the Escape handler
  // above. The layer is what keeps Alt+S off the Pro drawer's Save behind it.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const { customerPay, driverPay, additionalCosts } = margin;
  const marginLimit = margin.margin.marginLimit;

  // Employee (payroll-hourly) legs are priced separately: rate=0 with the real
  // cost in legPay. They get their own section (V1 main.pl:13371-13416) instead
  // of appearing as phantom "$0.00" rows in the commissioned Driver Pay list.
  // Both lists render in leg order (LEG 1 → LEG N); the backend returns legs in
  // settlement/creation order, so sort by legnum here. (`.filter` already
  // returns a fresh array, so the in-place sort doesn't mutate the read-model.)
  const byLegnum = (a: DriverPayLeg, b: DriverPayLeg) => a.legnum - b.legnum;
  const payLegs = driverPay.legs.filter((l) => !l.isEmployeeCost).sort(byLegnum);
  const empLegs = driverPay.legs.filter((l) => l.isEmployeeCost).sort(byLegnum);
  const empTotal = empLegs.reduce((sum, l) => sum + l.legPay, 0);

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Margin breakdown for PRO ${margin.pronumber}`}
      onClick={onClose}
      data-testid="pro-margin-modal"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <ModalHeader
          title={`Margin — PRO #${margin.pronumber}`}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          closeLabel="Close margin breakdown"
        />

        <div className="overflow-y-auto px-4 py-3 text-[0.78125rem] text-slate-700 dark:text-gray-200">
          {loading ? (
            <div className="py-12 text-center text-sm italic text-slate-400 dark:text-gray-500 animate-pulse">
              Loading…
            </div>
          ) : (
          <>
          {/* Customer Pay */}
          <ItemRateSection
            testid="margin-customer-pay"
            title="Customer Pay"
            items={customerPay.items}
            total={customerPay.total}
          />

          {/* Driver Pay */}
          <section data-testid="margin-driver-pay" className="mb-4">
            <SectionTitle>Driver Pay</SectionTitle>
            <table className="w-full">
              <thead>
                <tr>
                  <th className={`${TH} py-1`}>Item</th>
                  <th className={`${TH} ${NUM} py-1`}>Rate</th>
                  <th className={`${TH} py-1 pl-3`}>Fuel Surcharge</th>
                  <th className={`${TH} py-1 text-center`}>Comm?</th>
                </tr>
              </thead>
              <tbody>
                {payLegs.map((leg) => (
                  <LegRows key={`leg-${leg.legnum}`} leg={leg} />
                ))}
                {driverPay.lineItems.map((li, i) => (
                  <tr key={`li-${i}`} className={ROW_BORDER}>
                    <td className="py-1">
                      {li.item}
                      <WeTag isPaid={li.isPaid} payrollDate={li.payrollDate} />
                    </td>
                    <td className={`${NUM} py-1`}>
                      <RateOrState rate={li.rate} state={li.state} />
                    </td>
                    <td className="py-1 pl-3 text-[0.6875rem] text-slate-500 dark:text-gray-400">
                      {li.state === 'OK' && (li.fuelPct ?? 0) > 0
                        ? `${li.fuelType ?? ''} ${li.fuelPct}%`.trim()
                        : ''}
                    </td>
                    <td className="py-1 text-center">{(li.commission ?? 0) > 0 ? 'Y' : ''}</td>
                  </tr>
                ))}

                {/* Commission — flat 30% on every backend path (V1 hardcodes it). */}
                <tr className={ROW_BORDER}>
                  <td className="py-1">Commission - 30%</td>
                  <td className={`${NUM} py-1`}>{fmtUSD(-driverPay.commission)}</td>
                  <td />
                  <td />
                </tr>
                {/* Subtotal */}
                <tr className={`${ROW_BORDER} font-semibold`}>
                  <td className="py-1">Subtotal</td>
                  <td className={`${NUM} py-1`}>{fmtUSD(driverPay.subtotal)}</td>
                  <td />
                  <td />
                </tr>
                {/* Fuel Surcharge */}
                <tr className={ROW_BORDER}>
                  <td className="py-1">Fuel Surcharge</td>
                  <td className={`${NUM} py-1`}>{fmtUSD(driverPay.fuel)}</td>
                  <td />
                  <td />
                </tr>
                {/* Total */}
                <tr className={`${ROW_BORDER} font-bold`}>
                  <td className="py-1">Total</td>
                  <td className={`${NUM} py-1`}>{fmtUSD(driverPay.total)}</td>
                  <td />
                  <td />
                </tr>
              </tbody>
            </table>
          </section>

          {/* Employee Driver Cost (payroll-hourly legs) */}
          {empLegs.length > 0 && (
            <section data-testid="margin-employee-cost" className="mb-4">
              <SectionTitle>Employee Driver Cost</SectionTitle>
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={`${TH} py-1`}>Item</th>
                    <th className={`${TH} ${NUM} py-1`}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {empLegs.map((leg) => (
                    <tr key={`emp-${leg.legnum}`} className={ROW_BORDER}>
                      <td className="py-1">LEG {leg.legnum}</td>
                      <td className={`${NUM} py-1`}>{fmtUSD(leg.legPay)}</td>
                    </tr>
                  ))}
                  <tr className={`${ROW_BORDER} font-bold`}>
                    <td className="py-1">Total</td>
                    <td className={`${NUM} py-1`}>{fmtUSD(empTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          )}

          {/* Additional Costs (carrier / accessorial). The section Total sums
              BOTH buckets — `total` (CARRIER RATE / FREIGHT BILL) and `fuel`
              (CARRIER FSC / ACCESSORIAL) — matching V1 main.pl:13366
              ($addcosttotal + $addcostfuel). The buckets stay split on the
              read-model because the margin summary consumes them separately. */}
          {additionalCosts.items.length > 0 && (
            <ItemRateSection
              testid="margin-additional-costs"
              title="Additional Costs"
              items={additionalCosts.items}
              total={additionalCosts.total + additionalCosts.fuel}
            />
          )}

          {/* Profit / Loss + Margin */}
          <section data-testid="margin-summary">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={`${TH} py-1`} />
                  <th className={`${TH} ${NUM} py-1`}>Profit/Loss</th>
                  <th className={`${TH} ${NUM} py-1`}>Margin</th>
                </tr>
              </thead>
              <tbody>
                <SummaryRow
                  testid="margin-cell-customer"
                  label="Customer Charges"
                  row={margin.margin.customerCharges}
                  marginLimit={marginLimit}
                />
                <SummaryRow
                  testid="margin-cell-fuel"
                  label="Fuel"
                  row={margin.margin.fuel}
                  marginLimit={marginLimit}
                />
                <SummaryRow
                  testid="margin-cell-total"
                  label="Total"
                  row={margin.margin.total}
                  marginLimit={marginLimit}
                  bold
                />
              </tbody>
            </table>
          </section>
          </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** One driver-pay leg: its base row plus a row per add-on (DAYCAB, HAZMAT, …). */
const LegRows: React.FC<{ leg: DriverPayLeg }> = ({ leg }) => {
  const fuel =
    leg.state === 'OK' && leg.fuelPct > 0
      ? `${leg.fuelType} ${leg.fuelPct}%`.trim()
      : '';
  return (
    <>
      <tr className={ROW_BORDER}>
        <td className="py-1">
          LEG {leg.legnum}
          <WeTag isPaid={leg.isPaid} payrollDate={leg.payrollDate} />
        </td>
        <td className={`${NUM} py-1`}>
          <RateOrState rate={leg.rate} state={leg.state} />
        </td>
        <td className="py-1 pl-3 text-[0.6875rem] text-slate-500 dark:text-gray-400">{fuel}</td>
        <td className="py-1 text-center">{leg.commission > 0 ? 'Y' : ''}</td>
      </tr>
      {leg.addons.map((a, i) => (
        <tr key={`addon-${leg.legnum}-${i}`} className={ROW_BORDER}>
          <td className="py-1 pl-3 text-slate-600 dark:text-gray-300">{a.item}</td>
          <td className={`${NUM} py-1`}>{fmtUSD(a.rate)}</td>
          <td />
          <td className="py-1 text-center">{a.commission > 0 ? 'Y' : ''}</td>
        </tr>
      ))}
    </>
  );
};

/** One profit/loss + margin summary row. Reds a row when its own margin % is
 * below the margin limit, regardless of sign — so a thin-but-positive margin
 * under the limit is still flagged. V1 (main.pl:13373) reds at the limit too;
 * gwi360 does not (SR-7685, see isBelowMarginLimit). */
const SummaryRow: React.FC<{
  testid: string;
  label: string;
  row: { profitLoss: number; marginPct: number };
  marginLimit: number;
  bold?: boolean;
}> = ({ testid, label, row, marginLimit, bold }) => {
  const red = isBelowMarginLimit(row.marginPct, marginLimit);
  return (
    <tr className={`${ROW_BORDER} ${bold ? 'font-bold' : ''}`}>
      <td className="py-1">{label}</td>
      <td className={`${NUM} py-1`}>{fmtUSD(row.profitLoss)}</td>
      <td className={`${NUM} py-1`}>
        <span
          data-testid={testid}
          className={`inline-block rounded px-1.5 ${
            red
              ? 'bg-red-600 font-semibold text-white dark:bg-red-700'
              : 'text-emerald-700 dark:text-emerald-400'
          }`}
        >
          {fmtPct(row.marginPct)}
        </span>
      </td>
    </tr>
  );
};

export default ProMarginModal;
